import { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Trash2, Plus, Upload, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlacklistCedulas() {
  const navigate = useNavigate();
  const [cedulas, setCedulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCedula, setNewCedula] = useState('');
  const [newRazon, setNewRazon] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadBlacklist();
  }, []);

  const loadBlacklist = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cedulas/blacklist`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCedulas(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCedula = async () => {
    if (!newCedula) {
      toast.error('Ingresa una cédula');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/cedulas/blacklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cedula: newCedula.trim(),
          razon: newRazon.trim() || 'No especificada'
        })
      });

      if (response.ok) {
        toast.success('Cédula agregada a la blacklist');
        setNewCedula('');
        setNewRazon('');
        loadBlacklist();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al agregar cédula');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleDeleteCedula = async (cedula) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cedulas/blacklist/${encodeURIComponent(cedula)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Cédula removida de la blacklist');
        loadBlacklist();
      } else {
        toast.error('Error al eliminar cédula');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      toast.error('Solo se permiten archivos .txt');
      e.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      const text = await file.text();
      const cedulasFromFile = text.split('\n')
        .map(line => line.trim())
        .filter(line => line && line.length >= 8);

      if (cedulasFromFile.length === 0) {
        toast.error('El archivo no contiene cédulas válidas');
        setUploadingFile(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/cedulas/blacklist/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cedulas: cedulasFromFile })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.added} cédulas agregadas a la blacklist`);
        loadBlacklist();
        e.target.value = '';
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al procesar archivo');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al procesar archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-6xl mx-auto">
        {/* Botón Volver */}
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="border-gray-700 text-gray-300 hover:bg-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-500" />
            Blacklist de Cédulas
          </h1>
          <p className="text-gray-400">
            Gestiona cédulas bloqueadas que no pueden crear expedientes ni agendar citas
          </p>
        </div>

        {/* Agregar Cédula */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Agregar Cédula a Blacklist</CardTitle>
            <CardDescription className="text-gray-400">
              Las cédulas bloqueadas no podrán crear expedientes ni agendar citas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Agregar manualmente */}
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Cédula *</label>
                  <Input
                    placeholder="11.111.111"
                    value={newCedula}
                    onChange={(e) => setNewCedula(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newCedula) {
                        handleAddCedula();
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Razón (opcional)</label>
                  <Input
                    placeholder="Ej: Error en documentación"
                    value={newRazon}
                    onChange={(e) => setNewRazon(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddCedula}
                className="bg-red-500 hover:bg-red-600 text-white w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Bloquear Cédula
              </Button>
            </div>

            {/* Upload archivo */}
            <div className="border-t border-gray-700 pt-4">
              <label className="text-sm text-gray-300 mb-3 block">Importar desde archivo .txt</label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 hover:border-red-500/50 transition-colors">
                <input
                  type="file"
                  id="cedulas-upload"
                  accept=".txt"
                  onChange={handleUploadFile}
                  className="hidden"
                  disabled={uploadingFile}
                />
                <label htmlFor="cedulas-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <p className="text-gray-300 font-medium">
                      {uploadingFile ? 'Procesando archivo...' : 'Subir archivo .txt con cédulas'}
                    </p>
                    <p className="text-xs text-gray-500 text-center">
                      Una cédula por línea. Ejemplo:<br />
                      11.111.111<br />
                      22.222.222
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Cédulas Bloqueadas */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Cédulas Bloqueadas ({cedulas.length})</CardTitle>
                <CardDescription className="text-gray-400">
                  Estas cédulas están bloqueadas permanentemente
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {cedulas.length === 0 ? (
              <div className="bg-gray-900/30 rounded-lg p-12 text-center">
                <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay cédulas bloqueadas</p>
                <p className="text-gray-500 text-sm mt-2">
                  Las cédulas agregadas aquí no podrán generar expedientes ni citas
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {cedulas.map((item) => (
                  <div
                    key={item.cedula}
                    className="flex items-center justify-between bg-gray-900/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/30 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Shield className="w-4 h-4 text-red-400" />
                        <span className="text-white font-semibold font-mono">{item.cedula}</span>
                      </div>
                      {item.razon && (
                        <p className="text-sm text-gray-400 ml-7">
                          <AlertTriangle className="w-3 h-3 inline mr-1 text-yellow-500" />
                          {item.razon}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-7 mt-1">
                        Agregada: {new Date(item.added_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteCedula(item.cedula)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
